import React, {useState, useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {Button, Modal, Form, Table} from "react-bootstrap";
import {useSuppliers} from "../../SupplierContext";
// import axios from "axios";
import '../../App.css';
import { FaCheckSquare, FaTimesCircle } from "react-icons/fa";
import {apiInstance} from "../../api/api";
import {useTransactions} from "../../contextjs/TransactionContext";
import {useAccounts} from "../../contextjs/AccountContext";
import {useUser} from "../../UserContext";

function Supplier() {
    const {user} = useUser();
    const {suppliers, fetchAllSuppliers, createSupplier, updateSupplierDetails, removeSupplier,} = useSuppliers();
    const [showSupplierModal, setShowSupplierModal] = useState(false);
    const [newSupplier, setNewSupplier] = useState({
        supplier_name: '',
        email: '',
        phone_number: '',
        sales_rep: ''
    });
    const [showEditSupplierModal, setShowEditSupplierModal] = useState(false);
    const [currentSupplier, setCurrentSupplier] = useState(null);
    const [searchSupplierTerm, setSearchSupplierTerm] = useState(''); //search term for suppliers
    // payment modal states
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [transactionCharges, setTransactionCharges] = useState(0);
    // to immediately re-fetch transactions and accounts after a payment
    const { loadTransactions } = useTransactions();
    const { fetchAccountsData } = useAccounts();
    const isAdmin = Boolean(
        user?.is_superuser ||
        user?.groups?.includes?.('Admin') ||
        user?.groups?.includes?.('Administrator')
    );

    const paymentMethods = [
        {value: 'Cash', label: 'Cash'},
        {value: 'Mpesa Till', label: 'Mpesa Till'},
        {value: 'George Mpesa', label: 'George Mpesa'},
        // {value: 'KCB Cheque', label: 'KCB Cheque'},
        // {value: 'Equity Cheque', label: 'Equity Cheque'},
        {value: 'KCB', label: 'KCB'},
        {value: 'Equity', label: 'Equity'},
    ];

    const paymentMethodsRequiringCharges = ['Mpesa Till', 'George Mpesa'];

    const supplierModalRef = useRef(); //reference to the modal for adding suppliers
    const editSupplierModalRef = useRef(); //reference to the modal for editing suppliers

    //function to detect and handle click outside the modal
    const handleClickOutside = (e, modalRef, closeModal) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
            closeModal();
        }
    };

    //useEffect hook to handle click outside event
    useEffect(() => {
        const onOutsideClick = (e) => {
            handleClickOutside(e, supplierModalRef, () => setShowSupplierModal(false));
            handleClickOutside(e, editSupplierModalRef, () => setShowEditSupplierModal(false));
        };

        document.addEventListener('mousedown', onOutsideClick);
        return () => {
            document.removeEventListener('mousedown', onOutsideClick);
        };
    }, []);

    useEffect(() => {
        fetchAllSuppliers();
    }, [fetchAllSuppliers]);

    const navigate = useNavigate();

    const handleBack = () => {
        navigate(-1);
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            await createSupplier(newSupplier);
            setShowSupplierModal(false);
            // reset the newSupplier state to clear the form
            setNewSupplier({
                supplier_name: '',
                email: '',
                phone_number: '',
                sales_rep: ''
            });
        } catch (error) {
            console.log('Error adding supplier: ', error);
        }
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setNewSupplier(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleEditInputChange = (e) => {
        const {name, value} = e.target;
        setCurrentSupplier(prevState => ({
            ...prevState,
            [name]: value
        }));
    }

    const handleShowEditModal = async (supplier) => {
        setCurrentSupplier(supplier);
        setShowEditSupplierModal(true);
    };

    const handleHideEditModal = () => {
        setShowEditSupplierModal(false);
    }

    const handleUpdateSupplier = async (e) => {
        e.preventDefault();
        try {
            await updateSupplierDetails(currentSupplier);
            handleHideEditModal();
        } catch (error) {
            console.log('Error updating supplier: ', error);
        }
    };

    const handleDeleteSupplier = async (supplierId) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this supplier?');
        if (confirmDelete) {
            try {
                await removeSupplier(supplierId);
            } catch (error) {
                console.log('Error deleting supplier: ', error);
                alert(error?.response?.data?.detail || 'This supplier cannot be deleted');
            }
        }
    };

    const filteredSuppliers = suppliers.filter(supplier => {
        const matchSupplierName = supplier.supplier_name.toLowerCase().includes(searchSupplierTerm.toLowerCase());
        const matchSalesRep = supplier.sales_rep.toLowerCase().includes(searchSupplierTerm.toLowerCase());
        return matchSupplierName || matchSalesRep;
    });

    // helper function to check if a supplier has an outstanding balance
    const supplierHasOutStandingBalance = (supplier) => {
        // logic to determine if a supplier has an outstanding balance
        const balance = parseFloat(supplier.balance) || 0;
        return balance > 0;
    };

    const handleShowPaymentModal = (supplier) => {
        setShowPaymentModal(true);
        setCurrentSupplier(supplier);
    };

    const handleHidePaymentModal = () => {
        setShowPaymentModal(false);
    };

    const handleSupplierPaymentSubmit = async (e) => {
        e.preventDefault();
        const paymentData = {
            supplier: currentSupplier.id,
            amount: paymentAmount,
            payment_method: paymentMethod,
            transaction_charges: transactionCharges || 0,
        };

        try {
            // send payment data to the server
            await apiInstance.post('/supplierpayments/', paymentData);
            // refresh supplier data
            await fetchAllSuppliers();
            setShowPaymentModal(false);
            // reset form fields
            setPaymentAmount('');
            setPaymentMethod('');
            setTransactionCharges(0);
            // refresh transactions and accounts data
            await loadTransactions();
            await fetchAccountsData();
        } catch(error) {
            console.log('Error paying supplier: ', error);
        }
    };

    return(
        <div>
            <div className = "back-button-container">
                <Button variant="danger" onClick={handleBack}>Back</Button>
            </div>
            <h1>Suppliers</h1>
            <Button variant="primary" onClick={() => setShowSupplierModal(true)}>Add Supplier</Button>
            <div className="product-controls mt-3 mb-3">
                <Form.Control
                    type="text"
                    placeholder="Search suppliers"
                    value={searchSupplierTerm}
                    onChange={(e) => setSearchSupplierTerm(e.target.value)}
                />
            </div>
            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th>Status</th>
                        <th>Supplier Name</th>
                        <th>Email</th>
                        <th>Phone Number</th>
                        <th>Sales rep</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredSuppliers.map((supplier) => (
                        <tr key={supplier.id}>
                            <td>
                            {/*status icon*/}
                                {supplierHasOutStandingBalance(supplier) ? (
                                    <FaTimesCircle style={{color: 'red'}} title="Outstanding Balance" />
                                ) : (
                                    <FaCheckSquare style={{color: 'green'}} title="No Outstanding Balance" />
                                )}
                            </td>
                            <td>
                                <Button variant="link" onClick={() => handleShowEditModal(supplier)}>
                                    {supplier.supplier_name}</Button>
                                <div className={`small ${Number(supplier.balance) > 0 ? 'text-danger' : Number(supplier.balance) < 0 ? 'text-success' : 'text-muted'}`}>
                                    {Number(supplier.balance) > 0
                                        ? `Remaining: KES ${Number(supplier.balance).toLocaleString()}`
                                        : Number(supplier.balance) < 0
                                            ? `Excess paid: KES ${Math.abs(Number(supplier.balance)).toLocaleString()}`
                                            : 'Balanced'}
                                </div>
                            </td>
                            <td>{supplier.email}</td>
                            <td>{supplier.phone_number}</td>
                            <td>{supplier.sales_rep}</td>
                            <td>
                                <Button variant="success" onClick={() => handleShowEditModal(supplier)}>Edit</Button>
                                {isAdmin && supplier.can_delete && (
                                    <Button variant="danger" onClick={() => handleDeleteSupplier(supplier.id)}>Delete</Button>
                                )}
                                <Button variant="primary" onClick={() => handleShowPaymentModal(supplier)}>Pay Supplier</Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            {/*form for adding/updating suppliers would go here*/}
            <Modal show={showSupplierModal} onHide={() => setShowSupplierModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add Supplier</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Supplier Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="supplier_name"
                                value={newSupplier.supplier_name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={newSupplier.email}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control
                                type="text"
                                name="phone_number"
                                value={newSupplier.phone_number}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Sales Rep</Form.Label>
                            <Form.Control
                                type="text"
                                name="sales_rep"
                                value={newSupplier.sales_rep}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Button variant="success" type="submit">Add Supplier</Button>
                    </Form>
                </Modal.Body>
            </Modal>

            {/*supplier payment modal component*/}
            <Modal show={showPaymentModal} onHide={handleHidePaymentModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Pay Supplier - {currentSupplier?.supplier_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                {/* payment form*/}
                    <Form onSubmit={handleSupplierPaymentSubmit}>
                        <Form.Group>
                            <Form.Label>Amount</Form.Label>
                            <Form.Control
                                type="number"
                                name="amount"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Payment Method</Form.Label>
                            <Form.Control
                                as="select"
                                name="payment_method"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                required
                            >
                                <option value="">Select Payment Method</option>
                                {paymentMethods.map((method) => (
                                    <option key={method.value} value={method.value}>
                                        {method.label}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        {/* transaction charges if applicable */}
                        {paymentMethodsRequiringCharges.includes(paymentMethod) && (
                            <Form.Group>
                                <Form.Label>Transaction Charges</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="transaction_charges"
                                    value={transactionCharges}
                                    onChange={(e) => setTransactionCharges(e.target.value)}
                                />
                            </Form.Group>
                        )}
                        <Button variant="success" type="submit">Pay Supplier</Button>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showEditSupplierModal} onHide={handleHideEditModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Supplier</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentSupplier && (
                        <>
                            <Form onSubmit={handleUpdateSupplier}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Supplier Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="supplier_name"
                                        value={currentSupplier.supplier_name}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={currentSupplier.email}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Phone Number</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone_number"
                                        value={currentSupplier.phone_number}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Sales Rep</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="sales_rep"
                                        value={currentSupplier.sales_rep}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </Form.Group>
                                <Button variant="success" type="submit">Update Supplier</Button>
                            </Form>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default Supplier;